import { userPendingPhoneVerification } from '@/composables/auth';
import type { LocationQueryValue, RouteRecordRaw } from 'vue-router';

type UnauthenticatedRoute = RouteRecordRaw & {
    meta?: { showSignInButton?: boolean };
};

export const routes: UnauthenticatedRoute[] = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: {
            title: 'Login',
        },
        beforeEnter: (to, from, next) => {
            // This is a redirect from Pay OAuth, the user was accepting invitation and returned with auth token along with the invitation details

            if (to.query.invitation_id && to.query.invitation_token) {
                const updatedQuery: {
                    [x: string]: LocationQueryValue | LocationQueryValue[];
                } = {
                    ...to.query,
                    authorizationToken: to.query.token,
                    invitationToken: to.query.invitation_token,
                };

                delete updatedQuery.token;
                delete updatedQuery.invitation_token;

                next({
                    name: 'AcceptInvitation',
                    params: {
                        invitationId: String(to.query.invitation_id),
                    },
                    query: updatedQuery,
                });
                return;
            }

            // This is a redirect from Pay OAuth, the business was not whitelisted yet
            if (to.query.error === 'business_not_whitelisted') {
                next({ name: 'ThankYouForYourInterest' });
                return;
            }

            next();
        },
    },
    {
        path: '/invitations/:invitationId/accept',
        name: 'AcceptInvitation',
        component: () => import('@/views/AcceptInvitation.vue'),
        meta: {
            title: 'Accept Invitation',
            showSignInButton: true,
        },
        beforeEnter: (to, from, next) => {
            // This is a redirect from Pay OAuth, the business was not whitelisted yet
            if (to.query.error === 'business_not_whitelisted') {
                next({ name: 'ThankYouForYourInterest' });
                return;
            }

            // Rename token to invitationToken on FE
            // to differentiate between user authentication token returned from BE as "token" and the invitation token
            if (to.query.token) {
                const newQuery = { ...to.query };
                newQuery.invitationToken = newQuery.token;
                delete newQuery.token;

                next({
                    ...to,
                    query: newQuery,
                });

                return;
            }

            next();
        },
    },
    {
        path: '/verify-otp',
        name: 'VerifyOTP',
        component: () => import('@/views/VerifyOTP.vue'),
        meta: {
            title: 'Verify OTP',
        },
        beforeEnter: (to, from, next) => {
            if (!userPendingPhoneVerification.value) {
                next({ name: 'login' });
                return;
            }

            next();
        },
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPassword.vue'),
        meta: {
            title: 'Forgot Password',
        },
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/ResetPassword.vue'),
        meta: {
            title: 'Reset Password',
        },
    },
    {
        path: '/update-phone-number',
        name: 'UpdatePhoneNumber',
        component: () => import('@/views/UpdatePhoneNumber.vue'),
        meta: {
            title: 'Update Phone Number',
        },
    },
    {
        path: '/thank-you-for-your-interest',
        name: 'ThankYouForYourInterest',
        component: () => import('@/views/ThankYouForYourInterest.vue'),
        meta: {
            title: 'Thank You For Your Interest',
            showSignInButton: true,
        },
    },
];
